import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

const API = process.env.REACT_APP_API_BASE;

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '';
  return Number(value).toLocaleString('en-US');
}

function DealChecker() {
  const [form, setForm] = useState({
    vin: '',
    mileage: '',
    zip: '',
    askingPrice: '',
    accidentStatus: 'no'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.gtag?.('event', 'view_deal_checker');
  }, []);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);

    const payload = {
      vin: form.vin.trim(),
      mileage: Number(form.mileage),
      zip: form.zip.trim(),
      askingPrice: form.askingPrice ? Number(form.askingPrice) : null,
      accidentStatus: form.accidentStatus
    };

    if (!payload.vin || !payload.mileage || !payload.zip) {
      window.gtag?.('event', 'deal_checker_error', { error_type: 'validation' });
      setError('VIN, mileage, and ZIP are required.');
      return;
    }

    window.gtag?.('event', 'deal_checker_submit', {
      has_asking_price: payload.askingPrice !== null,
      accident_status: payload.accidentStatus,
    });

    setLoading(true);
    try {
      const response = await fetch(`${API}/deal-checker/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        window.gtag?.('event', 'deal_checker_error', { error_type: 'rate_limited' });
        throw new Error('Too many requests. Please try again later.');
      }

      if (!response.ok) {
        window.gtag?.('event', 'deal_checker_error', { error_type: 'http_error' });
        const details = Array.isArray(data.details) ? data.details.join(' ') : data.message || 'Could not check this deal.';
        throw new Error(details);
      }

      const compsCount = Number(data.compsUsedCount ?? data.compsUsed?.length ?? 0) || 0;
      const hasEstimate = data.mestimate !== null && data.mestimate !== undefined;
      const hasDealAnalysis = Boolean(data.dealAnalysis);
      const exactVehicleMatch = Boolean(data.meshumVehicle?.exists);

      if (hasEstimate || compsCount > 0) {
        window.gtag?.('event', 'deal_checker_result', {
          has_estimate: hasEstimate,
          has_deal_analysis: hasDealAnalysis,
          exact_vehicle_match: exactVehicleMatch,
          comps_count: compsCount,
          radius_used: data.radiusUsed ?? null,
          price_position: data.dealAnalysis?.pricePosition || '',
        });
      } else {
        window.gtag?.('event', 'deal_checker_no_result', {
          reason: 'no_estimate_or_comparables',
          exact_vehicle_match: exactVehicleMatch,
          comps_count: compsCount,
        });
      }

      setResult(data);
    } catch (err) {
      if (err instanceof TypeError) {
        window.gtag?.('event', 'deal_checker_error', { error_type: 'network_error' });
      }
      setError(err.message || 'Could not check this deal.');
    } finally {
      setLoading(false);
    }
  };

  const decoded = result?.decodedVehicle;
  const analysis = result?.dealAnalysis;
  const hasEstimate = result?.mestimate !== null && result?.mestimate !== undefined;

  return (
    <>
      <Helmet>
        <title>Used Car VIN Value & Deal Checker | Meshum</title>
        <meta
          name="description"
          content="Enter a VIN, mileage, and ZIP to estimate a used car's market value using recent sold comparable sales. Add an asking price to compare it with the Meshum Estimate."
        />
        <link rel="canonical" href="https://meshum.com/deal-checker" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Used Car VIN Value & Deal Checker | Meshum" />
        <meta
          property="og:description"
          content="Enter a VIN, mileage, and ZIP to estimate a used car's market value using recent sold comparable sales."
        />
        <meta property="og:url" content="https://meshum.com/deal-checker" />
        <meta property="og:image" content="https://meshum.com/share-thumbnail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Used Car VIN Value & Deal Checker | Meshum" />
        <meta
          name="twitter:description"
          content="Enter a VIN, mileage, and ZIP to estimate a used car's market value using recent sold comparable sales."
        />
        <meta name="twitter:image" content="https://meshum.com/share-thumbnail.jpg" />
      </Helmet>

      <main className="bg-slate-50">
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
            <h1 className="text-4xl font-semibold text-slate-950">What's This Car Worth?</h1>
            <p className="mt-3 max-w-2xl text-lg text-slate-600">
              Enter the VIN, mileage, and ZIP to get a Meshum Estimate based on recently sold comparable vehicles. Buying this car? Add the asking price to see how it compares.
            </p>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[420px_1fr]">
          <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-semibold text-slate-800">VIN</span>
                <input
                  value={form.vin}
                  onChange={e => update('vin', e.target.value.toUpperCase())}
                  maxLength={17}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="17-character VIN"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-800">Mileage</span>
                <input
                  value={form.mileage}
                  onChange={e => update('mileage', e.target.value)}
                  type="number"
                  min="1"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Current mileage"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-800">ZIP</span>
                <input
                  value={form.zip}
                  onChange={e => update('zip', e.target.value)}
                  maxLength={5}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="ZIP code"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-800">Asking Price <span className="font-normal text-slate-500">(optional)</span></span>
                <input
                  value={form.askingPrice}
                  onChange={e => update('askingPrice', e.target.value)}
                  type="number"
                  min="1"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Seller's asking price"
                />
                <span className="mt-1 block text-xs leading-5 text-slate-500">
                  Buying this car? Add the asking price to see whether it is priced above or below the Meshum Estimate.
                </span>
              </label>

              <fieldset>
                <legend className="text-sm font-semibold text-slate-800">Accident</legend>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[
                    ['no', 'No'],
                    ['yes', 'Yes'],
                    ['unknown', 'Unknown']
                  ].map(([value, label]) => (
                    <label
                      key={value}
                      className={`flex cursor-pointer items-center justify-center rounded-md border px-3 py-2 text-sm font-semibold ${form.accidentStatus === value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700'}`}
                    >
                      <input
                        type="radio"
                        name="accidentStatus"
                        value={value}
                        checked={form.accidentStatus === value}
                        onChange={e => update('accidentStatus', e.target.value)}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                {loading ? 'Checking...' : 'Check This Car'}
              </button>
            </div>
          </form>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            {!result && !loading && (
              <div className="flex min-h-[360px] items-center justify-center text-center text-slate-500">
                Enter vehicle details to estimate market value. Add an asking price only if you want to compare a specific deal.
              </div>
            )}

            {loading && (
              <div className="flex min-h-[360px] items-center justify-center text-center text-slate-600">
                Calculating market value...
              </div>
            )}

            {result && (
              <div className="space-y-5">
                {decoded && (
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Decoded Vehicle</div>
                    <h2 className="mt-1 text-2xl font-semibold text-slate-950">
                      {[decoded.year, decoded.make, decoded.model, decoded.trim].filter(Boolean).join(' ')}
                    </h2>
                    {[decoded.engine, decoded.driveType].filter(Boolean).length > 0 && (
                      <p className="mt-1 text-sm text-slate-600">{[decoded.engine, decoded.driveType].filter(Boolean).join(' | ')}</p>
                    )}
                  </div>
                )}

                {result.meshumVehicle?.exists && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                    <div className="font-semibold text-emerald-900">We found this exact vehicle in Meshum.</div>
                    <a
                      className="mt-2 inline-block font-semibold text-emerald-700 underline"
                      href={result.meshumVehicle.url}
                      onClick={() => window.gtag?.('event', 'deal_checker_vehicle_click', {
                        car_id: result.meshumVehicle.carId,
                        has_estimate: hasEstimate,
                        price_position: analysis?.pricePosition || '',
                      })}
                    >
                      View Full Vehicle Details
                    </a>
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-md bg-slate-50 p-4">
                    <div className="text-sm text-slate-500">Meshum Estimate</div>
                    <div className="mt-1 text-3xl font-semibold text-slate-950">{hasEstimate ? formatCurrency(result.mestimate) : 'Unavailable'}</div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-4">
                    <div className="text-sm text-slate-500">Comp Count</div>
                    <div className="mt-1 text-3xl font-semibold text-slate-950">{result.compsUsedCount || 0}</div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-4">
                    <div className="text-sm text-slate-500">Radius Used</div>
                    <div className="mt-1 text-3xl font-semibold text-slate-950">{result.radiusUsed ? `${result.radiusUsed} mi` : 'None'}</div>
                  </div>
                </div>

                <p className="text-sm text-slate-600">{result.supportText}</p>

                {analysis && (
                  <div className="rounded-md border border-slate-200 p-4">
                    <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Asking Price Comparison</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      <div>
                        <div className="text-sm text-slate-500">Difference</div>
                        <div className="text-xl font-semibold text-slate-950">{analysis.differenceAmount === null ? 'Unavailable' : formatCurrency(analysis.differenceAmount)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Difference %</div>
                        <div className="text-xl font-semibold text-slate-950">{analysis.differencePercent === null ? 'Unavailable' : `${analysis.differencePercent}%`}</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Position</div>
                        <div className="text-xl font-semibold text-slate-950">{analysis.pricePosition}</div>
                      </div>
                    </div>
                  </div>
                )}

                {Array.isArray(result.compsUsed) && result.compsUsed.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">Comparable Sales Used</h3>
                    <div className="mt-3 divide-y divide-slate-200 rounded-md border border-slate-200">
                      {result.compsUsed.slice(0, 5).map(comp => (
                        <div key={comp.id} className="grid gap-3 p-3 sm:grid-cols-[72px_1fr_auto] sm:items-center">
                          <img src={comp.imageUrl} alt="" className="h-14 w-20 rounded object-cover" />
                          <div>
                            <div className="font-semibold text-slate-900">{[comp.year, comp.make, comp.model, comp.trim].filter(Boolean).join(' ')}</div>
                            <div className="text-sm text-slate-600">{formatNumber(comp.mileage)} miles</div>
                          </div>
                          <div className="font-semibold text-slate-950">{formatCurrency(comp.price)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}

export default DealChecker;
